/**
 * KpiCard - Card para exibicao de KPIs
 *
 * Exibe:
 * - Valor principal com icone
 * - Label descritivo
 * - Tendencia (opcional)
 * - Mini grafico (opcional)
 */

import { cloneElement } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

/**
 * Mini grafico de linha simples usando SVG
 */
const MiniChart = ({ data = [], color = '#00d4aa', height = 40, width = 100 }) => {
  if (!data || data.length < 2) return null;

  const values = data.map((d) => d.value || d);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  // Area preenchida
  const areaPoints = `0,${height} ${points} ${width},${height}`;

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      {/* Area preenchida */}
      <polygon
        points={areaPoints}
        fill={`${color}20`}
      />
      {/* Linha */}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Ponto final */}
      <circle
        cx={width}
        cy={height - ((values[values.length - 1] - min) / range) * height}
        r="3"
        fill={color}
      />
    </svg>
  );
};

/**
 * Indicador de tendencia
 */
const TrendIndicator = ({ trend, tokens }) => {
  if (!trend || trend.changePercent === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TrendingFlatIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
        <Typography sx={{ fontSize: '11px', color: tokens?.text?.tertiary }}>
          0%
        </Typography>
      </Box>
    );
  }

  const isPositive = trend.direction === 'up';
  const color = trend.isGoodWhenUp
    ? (isPositive ? tokens?.semantic?.success : tokens?.semantic?.error)
    : (isPositive ? tokens?.semantic?.error : tokens?.semantic?.success);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isPositive ? (
        <TrendingUpIcon sx={{ fontSize: 14, color }} />
      ) : (
        <TrendingDownIcon sx={{ fontSize: 14, color }} />
      )}
      <Typography sx={{ fontSize: '11px', color, fontWeight: 500 }}>
        {isPositive ? '+' : ''}{trend.changePercent}%
      </Typography>
    </Box>
  );
};

const KpiCard = ({
  icon,
  label,
  value,
  suffix = '',
  prefix = '',
  description,
  trend = null,
  isGoodWhenUp = true,
  chartData = null,
  chartColor,
  onClick,
  size = 'medium', // small, medium, large
  variant = 'default', // default, highlight, subtle
}) => {
  const tokens = usePremiumTokens();

  const sizeConfig = {
    small: {
      padding: 1.5,
      valueSize: '20px',
      labelSize: '11px',
      iconSize: 18,
      chartWidth: 60,
      chartHeight: 24,
    },
    medium: {
      padding: 2,
      valueSize: '28px',
      labelSize: '12px',
      iconSize: 22,
      chartWidth: 80,
      chartHeight: 32,
    },
    large: {
      padding: 2.5,
      valueSize: '36px',
      labelSize: '13px',
      iconSize: 28,
      chartWidth: 100,
      chartHeight: 40,
    },
  };

  const config = sizeConfig[size];

  const variantStyles = {
    default: {
      backgroundColor: tokens?.surface?.raised,
      border: `1px solid ${tokens?.border?.subtle}`,
    },
    highlight: {
      backgroundColor: `${tokens?.accent?.primary}15`,
      border: `1px solid ${tokens?.accent?.primary}40`,
    },
    subtle: {
      backgroundColor: tokens?.surface?.elevated,
      border: 'none',
    },
  };

  const trendWithGood = trend ? { ...trend, isGoodWhenUp } : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: config.padding,
        borderRadius: tokens?.radius?.md,
        ...variantStyles[variant],
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${tokens?.shadow?.md || 'rgba(0,0,0,0.1)'}`,
        } : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Lado esquerdo - Label e valor */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {icon && cloneElement(icon, {
              sx: {
                fontSize: config.iconSize,
                color: variant === 'highlight' ? tokens?.accent?.primary : tokens?.text?.tertiary,
              },
            })}
            <Typography
              sx={{
                color: tokens?.text?.secondary,
                fontSize: config.labelSize,
                fontWeight: 500,
              }}
            >
              {label}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            {prefix && (
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: config.valueSize, fontWeight: 300 }}>
                {prefix}
              </Typography>
            )}
            <Typography
              sx={{
                color: variant === 'highlight' ? tokens?.accent?.primary : tokens?.text?.primary,
                fontSize: config.valueSize,
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
            {suffix && (
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px', fontWeight: 400 }}>
                {suffix}
              </Typography>
            )}
          </Box>

          {/* Tendencia */}
          {trendWithGood && (
            <Box sx={{ mt: 0.5 }}>
              <TrendIndicator trend={trendWithGood} tokens={tokens} />
            </Box>
          )}

          {/* Descricao */}
          {description && (
            <Typography
              sx={{
                color: tokens?.text?.tertiary,
                fontSize: '11px',
                mt: 0.5,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Lado direito - Mini grafico */}
        {chartData && chartData.length > 1 && (
          <Box sx={{ ml: 2 }}>
            <MiniChart
              data={chartData}
              color={chartColor || tokens?.accent?.primary}
              width={config.chartWidth}
              height={config.chartHeight}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default KpiCard;
